import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { hasPricingAndPurchasingSucceededSelector } from 'pricing-pages-ui/selectors/LoadingStateSelectors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import SuiteProductLevelIdentifiersMap from 'pricing-pages-ui/constants/merchCards/SuiteProductLevelIdentifiersMap';
import SkeletonLoadWrapper from '../../common/loading/SkeletonLoadWrapper';
import { CrmSuitePrice } from '../../merch-card/price-display/CrmSuitePrice';
import { SUITE_CARD } from '../../../constants/tracking/Locations';
import { ENTERPRISE, FREE, PROFESSIONAL, STARTER } from 'self-service-api/constants/ProductLevels';
import { useQueryParamTerm } from '../../../utils/urls/queryParamUtils';
import SuiteTermSelectorContainer from '../../merch-card/SuiteTermSelectorContainer';
import { borderStyle, Section } from '../../merch-card/MerchCardSharedComponents';
import SuiteTileCTAsContainer from '../../merch-card/SuiteTileCTAsContainer';
import MobileSuiteBusinessObjectives from '../MobileSuiteBusinessObjectives';
import { useAppSelector } from '../../../reducers';
import CurrentPlanTag from '../../common/CurrentPlanTag';
import { ownsCrmSuiteProductLevel } from '../../../selectors/ProductInventorySelectors';
import { SectionTitle } from '../../common/SharedComponents';
import HR from 'UIComponents/elements/HR';
import styled from 'styled-components';
import { BATTLESHIP } from 'HubStyleTokens/colors';
import { useSelector } from 'react-redux';
import Small from 'UIComponents/elements/Small';
import { isProGWP2023EligibleSelector } from '../../../selectors/PromotionSelectors';
import MobileSuiteFeatureTable from '../MobileSuiteFeatureTable';
import { buildFilteredHighlightedProducts } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import * as ProductLines from 'self-service-api/constants/ProductLines';
export const StyledHR = styled(HR).withConfig({
  displayName: "MobileSuiteTile__StyledHR",
  componentId: "isppq8-0"
})(["background-color:", ";"], BATTLESHIP);

const getStringKeysForProductLevel = productLevel => {
  switch (productLevel) {
    case FREE:
      return {
        titleKey: 'merchCards.suiteTile.freeCard.title',
        subtitleKey: 'merchCards.suiteTile.freeCard.description'
      };

    case STARTER:
      return {
        titleKey: 'merchCards.suiteTile.starterCard.title',
        subtitleKey: 'merchCards.suiteTile.starterCard.description'
      };

    case PROFESSIONAL:
      return {
        titleKey: 'merchCards.suiteTile.professionalCard.title',
        subtitleKey: 'merchCards.suiteTile.professionalCard.description'
      };

    case ENTERPRISE:
      return {
        titleKey: 'merchCards.suiteTile.enterpriseCard.title',
        subtitleKey: 'merchCards.suiteTile.enterpriseCard.description'
      };

    default:
      return {
        titleKey: '',
        subtitleKey: ''
      };
  }
};

export const MobileSuiteTile = ({
  productLevel,
  commonStyles
}) => {
  const hasPricingAndPurchasingSucceeded = useAppSelector(hasPricingAndPurchasingSucceededSelector);
  const isCurrentPlanLevel = useAppSelector(state => ownsCrmSuiteProductLevel(state, {
    productLevel
  }));
  const {
    setQueryParamTerm,
    queryParamTerm
  } = useQueryParamTerm();
  const {
    titleKey,
    subtitleKey
  } = getStringKeysForProductLevel(productLevel);
  const productsByProductLine = {
    [ProductLines.MARKETING]: buildFilteredHighlightedProducts({
      productLine: ProductLines.MARKETING,
      productLevels: [productLevel]
    }),
    [ProductLines.SALES]: buildFilteredHighlightedProducts({
      productLine: ProductLines.SALES,
      productLevels: [productLevel]
    }),
    [ProductLines.SERVICE]: buildFilteredHighlightedProducts({
      productLine: ProductLines.SERVICE,
      productLevels: [productLevel]
    }),
    [ProductLines.OPERATIONS]: buildFilteredHighlightedProducts({
      productLine: ProductLines.OPERATIONS,
      productLevels: [productLevel]
    }),
    [ProductLines.CMS]: buildFilteredHighlightedProducts({
      productLine: ProductLines.CMS,
      productLevels: [productLevel]
    })
  };
  const isProGWP = useSelector(isProGWP2023EligibleSelector);
  const disclaimer = productLevel === PROFESSIONAL && isProGWP ? 'pricingPage.promotions.proGWPDisclaimer' : undefined;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Section, {
      "data-selenium-id": `${SuiteProductLevelIdentifiersMap[productLevel]}-merch-card`,
      className: "flex flex-column p-top-6",
      style: Object.assign({}, commonStyles, {
        borderTop: borderStyle,
        gridRowStart: 1,
        position: 'relative'
      }),
      children: [isCurrentPlanLevel && /*#__PURE__*/_jsx(CurrentPlanTag, {}), /*#__PURE__*/_jsx("div", {
        className: "display-flex align-center",
        children: /*#__PURE__*/_jsx(SectionTitle, {
          className: "m-bottom-0",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: titleKey
          })
        })
      })]
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-6",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 2
      }),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: subtitleKey
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-4",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 3
      }),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [16, 23, 16],
        widths: [60, 70, 130],
        className: "flex-column",
        Content: CrmSuitePrice,
        contentProps: {
          productLevel,
          useLargePrice: true
        }
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-6",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 4
      }),
      children: !isCurrentPlanLevel && /*#__PURE__*/_jsx("div", {
        className: "m-top-3",
        children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
          isLoading: !hasPricingAndPurchasingSucceeded,
          heights: [41],
          Content: SuiteTermSelectorContainer,
          contentProps: {
            productLevel,
            onChange: term => setQueryParamTerm(term),
            queryParamTerm,
            trackingSource: SUITE_CARD
          }
        })
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-4 p-bottom-6",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 5
      }),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [47],
        className: "m-bottom-7",
        children: /*#__PURE__*/_jsx(SuiteTileCTAsContainer, {
          productLevel: productLevel,
          trackingSource: SUITE_CARD,
          ctaStyles: {
            width: '100%',
            lineHeight: '23px'
          },
          hubsSignupProductOverride: SuiteProductLevelIdentifiersMap[productLevel].split('-').join('').toLowerCase()
        })
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-bottom-6 flex flex-column align-center",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 6
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-bottom-6 flex flex-column",
      style: Object.assign({}, commonStyles, {
        gridRowStart: 7
      })
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-y-5",
      style: Object.assign({
        gridRowStart: 8
      }, commonStyles),
      children: /*#__PURE__*/_jsx(MobileSuiteBusinessObjectives, {
        productLevel: productLevel,
        addSpacing: true
      })
    }), disclaimer && /*#__PURE__*/_jsx(Section, {
      style: Object.assign({}, commonStyles, {
        gridRowStart: 9
      }),
      children: /*#__PURE__*/_jsx(Small, {
        use: "help",
        className: "align-end p-bottom-6",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: disclaimer
        })
      })
    }), /*#__PURE__*/_jsxs(Section, {
      className: "text-center p-x-0 p-y-6",
      style: Object.assign({}, commonStyles, {
        gridRowStart: disclaimer ? 10 : 9
      }),
      children: [/*#__PURE__*/_jsx(StyledHR, {
        className: "m-y-4"
      }), /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(MobileSuiteFeatureTable, {
          productsByProductLine: productsByProductLine,
          productLevel: productLevel
        })
      })]
    })]
  });
};